import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaAngleDoubleLeft, FaAngleDoubleRight, FaUser } from 'react-icons/fa';
import { AuthContext } from '../AuthContext';
import { useTheme } from '../ThemeContext';
import Notification from '../components/Notification';
import { getApiBaseUrl, getApiEnvironment, setApiEnvironment } from '../config';
import { NAV_TREE, findActiveSubPage, sectionHref, subPageHref } from './navTree';
import { useInboxCounts } from '../Inbox/InboxCountsContext';
import MaskIcon from './MaskIcon';
import './SideNav.css';

const COLLAPSED_STORAGE_KEY = 'djb-side-nav-collapsed';

// Section anchors live inside tabs that only mount after navigation, so the
// target element may not exist yet on the first frame — retry briefly.
const scrollToAnchor = (anchorId) => {
  const deadline = Date.now() + 2000;
  const attempt = () => {
    const el = document.getElementById(anchorId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (Date.now() < deadline) {
      window.setTimeout(attempt, 100);
    }
  };
  attempt();
};

const readStoredCollapsed = () => {
  try {
    return window.sessionStorage.getItem(COLLAPSED_STORAGE_KEY) === '1';
  } catch (e) {
    return false;
  }
};

const Chevron = ({ open }) => (
  <svg
    className={`side-nav-chevron-icon${open ? ' open' : ''}`}
    viewBox="0 0 24 24"
    width="16"
    height="16"
    aria-hidden="true"
  >
    <path
      d="M8.6 5.6 15 12l-6.4 6.4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// DJB-264: to-do badge next to a nav entry; hidden at zero so it disappears
// as soon as the last open request is completed or deleted
const NavBadge = ({ count }) =>
  count > 0 ? (
    <span className="side-nav-badge" aria-label={`${count} to do`}>
      {count}
    </span>
  ) : null;

const toggledSet = (prev, key) => {
  const next = new Set(prev);
  if (next.has(key)) {
    next.delete(key);
  } else {
    next.add(key);
  }
  return next;
};

// DJB-106/107: the nav menu proper — the pages tree (page → sub-page →
// section, each level behind its chevron) plus the footer (Dark Mode,
// Settings, user-card, DJB-116/117/118).
//
// DJB-122: extracted from the desktop rail so the mobile slide-in overlay
// renders the exact same menu. `onNavigate` fires after any page/sub-page/
// section selection (not chevron toggles), letting the overlay close itself;
// the desktop rail simply omits it. `collapsed` is desktop-only.
export const NavMenu = ({ collapsed = false, onNavigate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Guarded so the nav still renders standalone (tests) without a provider
  const { authData, logout } = useContext(AuthContext) || {};
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  // DJB-264: open-request counts behind the Inbox badges
  const { counts } = useInboxCounts();

  const [openPages, setOpenPages] = useState(() => new Set());
  const [openSubPages, setOpenSubPages] = useState(() => new Set());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const settingsRef = useRef();
  const userCardRef = useRef();

  const apiEnvironment = getApiEnvironment();

  const activePage = NAV_TREE.find((page) => location.pathname.startsWith(page.path));
  const activeSubPage = findActiveSubPage(activePage, location);

  const subPageBadge = (subPage) => (subPage.badge ? counts[subPage.badge] || 0 : 0);
  const pageBadge = (page) => page.subPages.reduce((sum, s) => sum + subPageBadge(s), 0);

  // Keep the active page's sub-pages visible when navigating into it
  useEffect(() => {
    const page = NAV_TREE.find(
      (p) => p.subPages.length > 0 && location.pathname.startsWith(p.path)
    );
    if (page) {
      setOpenPages((prev) => (prev.has(page.key) ? prev : new Set(prev).add(page.key)));
    }
  }, [location.pathname]);

  // The Settings and user-card menus close on any outside click, like the
  // header dropdowns they replace
  useEffect(() => {
    if (!settingsOpen && !userMenuOpen) {
      return undefined;
    }
    const handleClickOutside = (event) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setSettingsOpen(false);
      }
      if (userCardRef.current && !userCardRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [settingsOpen, userMenuOpen]);

  const goToPage = (page) => {
    navigate(page.path);
    onNavigate?.();
  };

  const goToSubPage = (page, subPage) => {
    navigate(subPageHref(page, subPage));
    onNavigate?.();
  };

  // DJB-112: a section deep-link carries the anchor as ?section= so the target
  // page can expand that section (they default to collapsed) before we scroll
  const goToSection = (page, subPage, section) => {
    navigate(sectionHref(page, subPage, section));
    scrollToAnchor(section.anchor);
    onNavigate?.();
  };

  const addNotification = (message, type) => {
    const id = Date.now();
    setNotifications((prev) => [...prev, { id, message, type }]);

    // Automatically remove notification after 5 seconds
    setTimeout(() => {
      setNotifications((prev) => prev.filter((notification) => notification.id !== id));
    }, 5000);
  };

  const handleForceIndex = async () => {
    setSettingsOpen(false);
    try {
      const response = await fetch(`${getApiBaseUrl()}/reindex`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authData.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to force reindex');
      }

      addNotification('Reindexing started successfully!', 'success');
    } catch (error) {
      addNotification('Failed to start reindexing. Please try again.', 'error');
    }
  };

  // DJB-117: switching environments invalidates the session (the token only
  // works against the environment that issued it), so log out and reload
  const handleEnvironmentToggle = () => {
    const newEnvironment = apiEnvironment === 'prod' ? 'qa' : 'prod';
    setApiEnvironment(newEnvironment);
    setSettingsOpen(false);

    // Clear auth data
    logout();

    // Reload the page after a short delay to ensure logout completes
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  // DJB-189: deep-link to Accounts Overview with the marker param its Customize
  // Table panel opens on (MachineManager consumes and strips it)
  const handleCustomizeTable = () => {
    setSettingsOpen(false);
    navigate('/machines?tab=list&customize=table');
    onNavigate?.();
  };

  const handleSignOut = () => {
    setUserMenuOpen(false);
    logout();
  };

  return (
    <>
      <ul className="side-nav-pages">
        {NAV_TREE.map((page) => {
          const isActive = activePage?.key === page.key;
          const hasSubPages = page.subPages.length > 0;
          const pageOpen = openPages.has(page.key);
          const PageIcon = page.icon;

          return (
            <li key={page.key} className="side-nav-page">
              <div className={`side-nav-page-row${isActive ? ' is-active' : ''}`}>
                <button
                  type="button"
                  className="side-nav-page-link"
                  onClick={() => goToPage(page)}
                  title={collapsed ? page.label : undefined}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <span className="side-nav-icon-slot">
                    <PageIcon />
                  </span>
                  {!collapsed && (
                    <span className="side-nav-page-label">{page.label}</span>
                  )}
                  <NavBadge count={pageBadge(page)} />
                </button>
                {!collapsed && hasSubPages && (
                  <button
                    type="button"
                    className="side-nav-toggle side-nav-toggle--page"
                    onClick={() => setOpenPages((prev) => toggledSet(prev, page.key))}
                    aria-expanded={pageOpen}
                    aria-label={`${pageOpen ? 'Hide' : 'Show'} ${page.label} sub-pages`}
                  >
                    <Chevron open={pageOpen} />
                  </button>
                )}
              </div>
              {!collapsed && hasSubPages && pageOpen && (
                <ul className="side-nav-subpages">
                  {page.subPages.map((subPage) => {
                    const subKey = `${page.key}:${subPage.key}`;
                    const hasSections = subPage.sections.length > 0;
                    const subOpen = openSubPages.has(subKey);
                    // Own-route or ?tab= match; the first sub-page is the one
                    // its route opens by default
                    const subActive = isActive && activeSubPage?.key === subPage.key;

                    return (
                      <li key={subPage.key} className="side-nav-subpage">
                        <div
                          className={`side-nav-subpage-row${subActive ? ' is-active' : ''}`}
                        >
                          {hasSections ? (
                            <button
                              type="button"
                              className="side-nav-toggle side-nav-toggle--subpage"
                              onClick={() =>
                                setOpenSubPages((prev) => toggledSet(prev, subKey))
                              }
                              aria-expanded={subOpen}
                              aria-label={`${subOpen ? 'Hide' : 'Show'} ${subPage.label} sections`}
                            >
                              <Chevron open={subOpen} />
                            </button>
                          ) : (
                            <span className="side-nav-toggle-spacer" aria-hidden="true" />
                          )}
                          <button
                            type="button"
                            className="side-nav-subpage-link"
                            onClick={() => goToSubPage(page, subPage)}
                          >
                            {subPage.label}
                            <NavBadge count={subPageBadge(subPage)} />
                          </button>
                        </div>
                        {hasSections && subOpen && (
                          <ul className="side-nav-sections">
                            {subPage.sections.map((section) => (
                              <li key={section.anchor}>
                                <button
                                  type="button"
                                  className="side-nav-section-link"
                                  onClick={() => goToSection(page, subPage, section)}
                                >
                                  {section.label}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
      <div className="side-nav-footer">
        {/* DJB-118: Dark Mode sits above Settings — the whole row is one
            switch control, with the visual toggle track on the right */}
        <div className="side-nav-dark-mode">
          <div className="side-nav-page-row">
            <button
              type="button"
              role="switch"
              aria-checked={isDark}
              aria-label="Dark Mode"
              className="side-nav-page-link"
              onClick={toggleTheme}
              title={collapsed ? 'Dark Mode' : undefined}
            >
              <span className="side-nav-icon-slot">
                <MaskIcon src="/dark-mode.svg" />
              </span>
              {!collapsed && (
                <>
                  <span className="side-nav-page-label">Dark Mode</span>
                  <span
                    className={`side-nav-switch${isDark ? ' is-on' : ''}`}
                    aria-hidden="true"
                  >
                    <span className="side-nav-switch-thumb" />
                  </span>
                </>
              )}
            </button>
          </div>
        </div>
        {/* DJB-117: Settings sits above the user-card and re-homes the old
            header hamburger actions (Force Index + environment switch) */}
        <div className="side-nav-settings" ref={settingsRef}>
          <div className="side-nav-page-row">
            <button
              type="button"
              className="side-nav-page-link"
              onClick={() => setSettingsOpen((v) => !v)}
              title={collapsed ? 'Settings' : undefined}
              aria-haspopup="true"
              aria-expanded={settingsOpen}
            >
              <span className="side-nav-icon-slot">
                <MaskIcon src="/settings.svg" />
              </span>
              {!collapsed && <span className="side-nav-page-label">Settings</span>}
            </button>
          </div>
          {settingsOpen && (
            <div className="side-nav-menu" role="menu">
              <button type="button" role="menuitem" onClick={handleForceIndex}>
                Force Index
              </button>
              <button type="button" role="menuitem" onClick={handleEnvironmentToggle}>
                {apiEnvironment === 'prod'
                  ? 'Switch to TST Environment'
                  : 'Switch to PRD Environment'}
              </button>
              {/* DJB-189: opens the Accounts Overview Customize Table panel */}
              <button type="button" role="menuitem" onClick={handleCustomizeTable}>
                Customize Table
              </button>
            </div>
          )}
        </div>
        {/* DJB-116: user-card — template avatar (customization not supported
            yet), bold username over the role, chevron revealing Sign out */}
        {authData && (
          <div className="side-nav-user-card" ref={userCardRef}>
            <span className="side-nav-user-avatar" aria-hidden="true">
              <FaUser />
            </span>
            {!collapsed && (
              <span className="side-nav-user-text">
                <span className="side-nav-user-name">{authData.userName}</span>
                {/* The auth response carries no role yet — every login is an
                    admin of this console, so show that until it does */}
                <span className="side-nav-user-role">
                  {authData.userRole || 'Administrator'}
                </span>
              </span>
            )}
            <button
              type="button"
              className="side-nav-toggle side-nav-user-menu-toggle"
              onClick={() => setUserMenuOpen((v) => !v)}
              aria-haspopup="true"
              aria-expanded={userMenuOpen}
              aria-label="Open user menu"
            >
              <Chevron open={userMenuOpen} />
            </button>
            {userMenuOpen && (
              <div className="side-nav-menu" role="menu">
                <button type="button" role="menuitem" onClick={handleSignOut}>
                  Sign out
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="notification-container">
        {notifications.map((notification) => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            onClose={() =>
              setNotifications((prev) =>
                prev.filter((n) => n.id !== notification.id)
              )
            }
          />
        ))}
      </div>
    </>
  );
};

// DJB-106/107: desktop-only collapsible left nav. Collapsed = icons only;
// expanded = icon + bold-blue page name, with a right-side chevron per page
// revealing its sub-pages (bold dark-grey, left-side chevron revealing their
// sections). Mobile (≤900px) gets the header + slide-in overlay instead
// (DJB-122) — this component is hidden there via CSS.
//
// DJB-115/116/117: the rail is framed by a brand header (logo + collapse
// toggle) at the top and a footer at the bottom holding the Settings menu
// (the re-homed header hamburger actions) above the user-card (sign-out).
const SideNav = () => {
  // "Persists during the session" → sessionStorage, not localStorage
  const [collapsed, setCollapsed] = useState(readStoredCollapsed);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.sessionStorage.setItem(COLLAPSED_STORAGE_KEY, next ? '1' : '0');
      } catch (e) {
        // Storage unavailable — the toggle still works for this render tree
      }
      return next;
    });
  };

  return (
    <div className={`side-nav-rail${collapsed ? ' is-collapsed' : ''}`}>
      <nav
        className={`side-nav${collapsed ? ' is-collapsed' : ''}`}
        aria-label="Primary navigation"
      >
        {/* DJB-115: brand header — logo to the left of the collapse toggle;
            collapsed shows the logo-only mark, expanded the logo+name */}
        <div className="side-nav-brand">
          <img
            className="side-nav-brand-logo"
            src={collapsed ? '/DJ-logo.svg' : '/DJ-logo-with-name.svg'}
            alt="Digitale Jukebox"
          />
          <button
            type="button"
            className="side-nav-collapse-toggle"
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Expand navigation menu' : 'Collapse navigation menu'}
            title={collapsed ? 'Expand menu' : 'Collapse menu'}
          >
            {collapsed ? <FaAngleDoubleRight /> : <FaAngleDoubleLeft />}
          </button>
        </div>
        <NavMenu collapsed={collapsed} />
      </nav>
    </div>
  );
};

export default SideNav;
